import { ApiError } from '@central/api';
import { color } from '@central/ui';
import { Alert, Button, PasswordInput, TextInput } from '@mantine/core';
import { useMutation } from '@tanstack/react-query';
import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import { useEffect, useState, type FormEvent } from 'react';
import { api } from '../api';
import { errorMessage } from '../format';
import { AuthLayout, FormFrame } from './SignIn';

/** The server's password policy: 12 to 256 characters. */
const MIN_PASSWORD = 12;
const MAX_PASSWORD = 256;

const body = { margin: 0, fontSize: 14, lineHeight: '21px', color: color.textBody } as const;

/**
 * Invite acceptance (`/invite#<token>`): the invited person picks their name
 * and a password, and the account is created. The token travels in the URL
 * fragment, which browsers never send to a server; it is read once and
 * dropped from the address bar so it does not stay in history.
 */
export function Invite() {
  const hash = useRouterState({ select: (s) => s.location.hash });
  const navigate = useNavigate();
  const [token] = useState(() => hash.replace(/^#/, ''));
  useEffect(() => {
    if (hash) void navigate({ to: '/invite', hash: '', replace: true });
  }, [hash, navigate]);
  const [done, setDone] = useState(false);

  return (
    <AuthLayout>
      {!token ? (
        <InvalidLink />
      ) : done ? (
        <FormFrame title="You’re all set">
          <p style={body}>Your account is ready. Sign in with your email and the new password.</p>
          <Button component={Link} to="/sign-in" fullWidth>
            Sign in
          </Button>
        </FormFrame>
      ) : (
        <AcceptForm token={token} onDone={() => setDone(true)} />
      )}
    </AuthLayout>
  );
}

function InvalidLink() {
  return (
    <FormFrame title="This invite link is not valid">
      <p style={body}>
        It may have expired, been used already, or been replaced by a newer invite. Ask an Owner in
        your organization to send you a new link.
      </p>
      <Link to="/sign-in" style={{ color: color.link, fontSize: 14 }}>
        Go to sign in
      </Link>
    </FormFrame>
  );
}

function AcceptForm({ token, onDone }: { token: string; onDone: () => void }) {
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState<{ name?: string; password?: string }>({});
  const accept = useMutation({
    mutationFn: () => api.team.acceptInvite({ token, name: name.trim(), password }),
    onSuccess: onDone,
    onError: (e) => {
      if (e instanceof ApiError && e.fields) {
        setFieldErrors({ name: e.fields.name, password: e.fields.password });
      }
    },
  });
  if (accept.error instanceof ApiError && accept.error.status === 404) return <InvalidLink />;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const next = {
      name: name.trim() ? undefined : 'Enter your name.',
      password:
        password.length < MIN_PASSWORD
          ? `Use at least ${MIN_PASSWORD} characters.`
          : password.length > MAX_PASSWORD
            ? `Use at most ${MAX_PASSWORD} characters.`
            : undefined,
    };
    setFieldErrors(next);
    if (next.name || next.password) return;
    accept.mutate();
  };
  const err = accept.error;
  const fieldsShown = err instanceof ApiError && err.fields;

  return (
    <FormFrame title="Join your team on GoATMS Central">
      <p style={body}>Choose how you’ll appear to your team and a password for signing in.</p>
      <form
        onSubmit={submit}
        noValidate
        style={{ display: 'flex', flexDirection: 'column', gap: 18 }}
      >
        <TextInput
          label="Your name"
          autoComplete="name"
          value={name}
          error={fieldErrors.name}
          onChange={(e) => setName(e.currentTarget.value)}
        />
        <PasswordInput
          label="Password"
          description={`At least ${MIN_PASSWORD} characters.`}
          autoComplete="new-password"
          value={password}
          error={fieldErrors.password}
          onChange={(e) => setPassword(e.currentTarget.value)}
        />
        {err && !fieldsShown && <Alert color="red">{errorMessage(err)}</Alert>}
        <Button type="submit" fullWidth loading={accept.isPending}>
          Create account
        </Button>
      </form>
      <p style={{ ...body, color: color.textMuted, fontSize: 13 }}>
        Does your organization sign in through single sign-on? You can skip the password:{' '}
        <Link to="/sign-in" style={{ color: color.link }}>
          sign in
        </Link>{' '}
        with the email this invite was sent to.
      </p>
    </FormFrame>
  );
}
